import { useCreateTag, useSortedTags } from 'features/tags';
import React, { useCallback, useMemo } from 'react';
import { TagSearchMenu } from 'shared/components/ui/tag-search-menu';
import { defaultTagColorOption } from 'shared/constants';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { ID, IDPrefixes } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task, UpdateTask } from 'shared/types/task';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

// Draft tasks store tagIds directly on the task object (not via relationships)
const getDraftTaskTagIds = (task: Task): ID[] => {
  if ('tagIds' in task) {
    return (task as Task & { tagIds?: ID[] }).tagIds ?? [];
  }
  return [];
};

export type TagsSubMenuProps = Omit<
  React.ComponentProps<typeof TagSearchMenu>,
  'tags' | 'value' | 'onChange' | 'onCreate' | 'onOutside'
> & {
  tasks: Task[];
  onClose: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

export const TagsSubMenu: React.FC<TagsSubMenuProps> = ({
  tasks,
  onClose,
  onUpdate,
  ...rest
}) => {
  const tags = useSortedTags();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);
  const updateRelationships = useUpdateRelationshipsReferences();
  const { submit: createTag } = useCreateTag();

  // Compute tags that all selected tasks have in common
  const commonTagIds = useMemo(() => {
    if (!tasks.length) {
      return [];
    }

    // Capture IDs for each task
    const taskTagArrays = tasks.map((task) => {
      // If we are in "manual update" mode (e.g. draft tasks), use the property directly
      if (onUpdate && 'tagIds' in task) {
        return getDraftTaskTagIds(task);
      }

      // Otherwise use relationships locup
      const taskPrefixedId = createPrefixId(task.id, IDPrefixes.Task);
      const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
      return relatedIds
        .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
        .map((id) => getPrefixedId(id));
    });

    // Find intersection of all tag arrays
    return taskTagArrays.reduce<ID[]>(
      (common, taskTags) => common.filter((tagId) => taskTags.includes(tagId)),
      taskTagArrays[0] ?? [],
    );
  }, [tasks, relationshipsMap, onUpdate]);

  // Compute tags that some but not all selected tasks have
  const indeterminateTagIds = useMemo(() => {
    if (!tasks.length) {
      return [];
    }

    // Capture IDs for each task
    const taskTagArrays = tasks.map((task) => {
      if (onUpdate && 'tagIds' in task) {
        return getDraftTaskTagIds(task);
      }

      const taskPrefixedId = createPrefixId(task.id, IDPrefixes.Task);
      const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
      return relatedIds
        .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
        .map((id) => getPrefixedId(id));
    });

    // Indeterminate tags = (Union of all tags) - (Intersection of all tags)
    const allTagIds = new Set<ID>(taskTagArrays.flat());
    const commonTagSet = new Set(commonTagIds);
    return Array.from(allTagIds).filter((id) => !commonTagSet.has(id));
  }, [tasks, relationshipsMap, commonTagIds, onUpdate]);

  const handleTagChange = useCallback(
    (newTagIds: ID[]) => {
      // Determine which tags were added and which were removed
      const addedTagIds = newTagIds.filter((id) => !commonTagIds.includes(id));
      const removedTagIds = commonTagIds.filter(
        (id) => !newTagIds.includes(id),
      );
      // If we have an onUpdate handler, we update the tasks directly (Draft Mode)
      if (onUpdate) {
        tasks.forEach((task) => {
          const currentTags = new Set(getDraftTaskTagIds(task));

          addedTagIds.forEach((id) => currentTags.add(id));
          removedTagIds.forEach((id) => currentTags.delete(id));

          onUpdate(task, {
            id: task.id,
            tagIds: Array.from(currentTags),
          } as unknown as UpdateTask);
        });
        return;
      }

      // Build relationship updates
      const relationshipUpdates: RelationshipUpdate[] = [];

      // Handle added tags
      addedTagIds.forEach((tagId) => {
        const tagPrefixedId = createPrefixId(tagId, IDPrefixes.Tag);
        const taskPrefixedIds = tasks.map((task) =>
          createPrefixId(task.id, IDPrefixes.Task),
        );

        relationshipUpdates.push({
          id: tagPrefixedId,
          add: taskPrefixedIds,
        });
      });

      // Handle removed tags
      removedTagIds.forEach((tagId) => {
        const tagPrefixedId = createPrefixId(tagId, IDPrefixes.Tag);
        const taskPrefixedIds = tasks.map((task) =>
          createPrefixId(task.id, IDPrefixes.Task),
        );

        relationshipUpdates.push({
          id: tagPrefixedId,
          remove: taskPrefixedIds,
        });
      });

      if (relationshipUpdates.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [commonTagIds, tasks, updateRelationships, onUpdate],
  );

  const handleTagCreate = useCallback(
    async (name: string) => {
      if (!name) {
        return;
      }

      const newTag = await createTag({
        name,
        color: defaultTagColorOption.value,
      });

      // Add the newly created tag to all selected tasks
      handleTagChange([...commonTagIds, newTag.id]);
    },
    [createTag, commonTagIds, handleTagChange],
  );

  const handleClear = useCallback(() => {
    if (onUpdate) {
      tasks.forEach((task) => {
        onUpdate(task, {
          id: task.id,
          tagIds: [],
        } as unknown as UpdateTask);
      });
      return;
    }

    // Collect all tags (common + indeterminate) to remove
    const allTagsToRemove = [...commonTagIds, ...indeterminateTagIds];

    const relationshipUpdates: RelationshipUpdate[] = [];

    allTagsToRemove.forEach((tagId) => {
      const tagPrefixedId = createPrefixId(tagId, IDPrefixes.Tag);
      const taskPrefixedIds = tasks.map((task) =>
        createPrefixId(task.id, IDPrefixes.Task),
      );

      relationshipUpdates.push({
        id: tagPrefixedId,
        remove: taskPrefixedIds,
      });
    });

    if (relationshipUpdates.length) {
      updateRelationships(relationshipUpdates);
    }
  }, [commonTagIds, indeterminateTagIds, tasks, updateRelationships, onUpdate]);

  return (
    <TagSearchMenu
      {...rest}
      tags={tags}
      value={commonTagIds}
      indeterminateValue={indeterminateTagIds}
      onChange={handleTagChange}
      onClear={handleClear}
      onCreate={handleTagCreate}
      onOutside={onClose}
    />
  );
};
